/**
 * BriefApproveView — "here is what I am about to do; may I?"
 *
 * Story 26739. Visual contract: artboards (1) and (1b) in
 * `tasks/design/step-views.html`.
 *
 * Three blocks, in this order, and the order is the point:
 *
 *   A. WHAT ORA ALREADY RESOLVED — collapsed. Policy number, policy type, term
 *      dates, client, branch. These are Level 2 lookups, not extractions, so
 *      they carry no confidence chip and they are not what the Servicer is
 *      being asked about. Collapsed by default and openable, because "what did
 *      it decide on its own?" is a fair question that should cost one click,
 *      not a scroll past five lines they did not ask for.
 *   B. THE PROPOSED CHANGE — old beside new, with a confidence chip on the
 *      fields ora INFERRED from the email.
 *   C. ONE QUESTION, ONE COMMIT AFFORDANCE — the shared `DecisionCard`, passed
 *      in as a node. This view owns no transport, no latch and no payload
 *      shape; the host hands it the same card the drawer already renders, so
 *      an answer given here is the same answer given anywhere else.
 *
 * COLD SESSION (artboard 1b). When Epic's session has expired the host passes
 * a `credentialInset` — the shared `CredentialEntryCard` — which renders
 * directly above the commit affordance. Blocks A and B are identical. The pill
 * stays the only commit affordance; it just also signs in.
 *
 * No Office.js, no Remix server import (design Constraint 20).
 */
import type { ReactNode } from "react";
import { StepViewFrame, identityLine } from "./StepViewFrame";
import { OldNewDiff, type OldNewRow } from "./OldNewDiff";
import { NEEDS_YOU_LABEL } from "../statusLabels";

export interface BriefApproveViewProps {
  clientName?: string | null;
  policyNumber?: string | null;
  /** Block A. Field NAMES only — never their values (they are not the ask). */
  resolvedFields?: readonly string[];
  /** The "Change type · Replace mortgagee" line above the grid. */
  changeLead?: ReactNode;
  /** Block B. */
  diffRows?: readonly OldNewRow[];
  /**
   * Block C's prompt. The shared `DecisionCard` renders its OWN prompt, so
   * pass this only when you are not passing a card — otherwise the Servicer
   * reads the same question twice.
   */
  question?: ReactNode;
  /** Block C's commit affordance — the shared `DecisionCard`, as a node. */
  decisionCard?: ReactNode | null;
  /** Cold session: the credential inset, directly above the commit affordance. */
  credentialInset?: ReactNode | null;
  /** Extra content between the diff and the question (evidence, screenshots). */
  children?: ReactNode;
}

export const RESOLVED_SUMMARY_PREFIX = "ora resolved these";

export function BriefApproveView({
  clientName,
  policyNumber,
  resolvedFields,
  changeLead,
  diffRows,
  question,
  decisionCard,
  credentialInset,
  children,
}: BriefApproveViewProps) {
  const resolved = (resolvedFields ?? []).filter(
    (f) => typeof f === "string" && f.trim() !== "",
  );

  return (
    <StepViewFrame
      view="brief"
      eyebrow={NEEDS_YOU_LABEL}
      tone="attention"
      headline={identityLine(clientName, policyNumber)}
    >
      {/* Block A — collapsed by default. `<details>` rather than a controlled
          disclosure: it survives every re-render the status poll causes, which
          a `useState` open flag in a body that remounts does not. */}
      {resolved.length > 0 ? (
        <details
          className="rounded-[10px] border border-primary-gray-light-200 px-3 py-2"
          data-testid="brief-resolved-block"
        >
          <summary className="cursor-default list-none text-xs font-semibold text-primary-gray-light-900 marker:hidden">
            {`${RESOLVED_SUMMARY_PREFIX} · ${resolved.length} ${
              resolved.length === 1 ? "field" : "fields"
            }`}
          </summary>
          <ul className="mt-2 list-disc pl-4 text-[11.5px] leading-relaxed text-primary-gray-light-700">
            {resolved.map((field) => (
              <li key={field}>{field}</li>
            ))}
          </ul>
        </details>
      ) : null}

      {/* Block B */}
      {diffRows && diffRows.length > 0 ? (
        <OldNewDiff
          heading="ora's proposed change"
          lead={changeLead}
          rows={diffRows}
        />
      ) : null}

      {children}

      {/* Block C */}
      {question ? (
        <p
          className="text-[12.5px] leading-relaxed text-primary-gray-light-900"
          data-testid="brief-question"
        >
          {question}
        </p>
      ) : null}

      {credentialInset ? (
        <div data-testid="brief-credential-inset">{credentialInset}</div>
      ) : null}

      {decisionCard ?? null}
    </StepViewFrame>
  );
}

export default BriefApproveView;
